import {
  Ref,
  computed,
  useContext,
  readonly,
  ssrRef,
} from '@nuxtjs/composition-api'
import { useConfig } from '../useConfig'
import { Product, ProductAvailability } from '~/types/product'
import { ReactiveValue } from '~/types/common'
import { checkOrderable } from '~/lib/api/deserializers/product'

export const MAX_QUANTITY = 999

export enum ProductEvents {
  Search = 'product/search',
  ViewList = 'product/viewList',
  Click = 'product/click',
  ViewDetails = 'product/viewDetails',
}

export const useProduct = (
  productRef: ReactiveValue<Product>,
  cartQuantityRef?: Ref<number>
  // inputQuantityRef?: Ref<number>
) => {
  // inputQuantityRef ??= cartQuantityRef
  const { config } = useConfig()

  // Availability
  const isOrderable = computed(() => {
    return checkOrderable(productRef.value)
  })
  const webStock = computed(() => getStock(config.value?.webStoreId))
  const availability = computed(() => {
    if (!isOrderable.value) return ProductAvailability.NotOrderable

    if (productRef.value.inStock) return ProductAvailability.InStock

    return ProductAvailability.OutOfStock
  })
  // const deliveryStock = computed(() => webStock.value)
  // const pickupStock = computed(() => webStock.value)
  const maxQuantity = computed(() =>
    productRef.value.maxStockEnabled
      ? productRef.value.stockQuantity
      : MAX_QUANTITY
  )
  const maxQuantityInc = computed(() => {
    const quantityInc = maxQuantity.value - (cartQuantityRef?.value ?? 0)
    return quantityInc > 0 ? quantityInc : 0
  })

  const getStock = (storeId?: number) => {
    const stock = productRef.value.stock
    if (!storeId || !stock) return 0

    const store = stock.find((st) => st.storeId === storeId)
    if (!store) return 0

    return store.quantity > 0 ? store.quantity : 0
  }

  return {
    isOrderable,
    maxQuantity,
    maxQuantityInc,
    webStock,
    availability,
    // deliveryStock,
    // pickupStock,

    getStock,
  }
}


export const useProductStockRefresh = (
  productParam: Ref<Product[]> | Ref<Product | null>) => {
  const products = computed(() => {
    const asProducts = productParam.value as Product[]
    if (typeof asProducts?.length === 'undefined') {
      if (productParam.value === null) {
        return []
      } else {
        return [ productParam.value as Product ]
      }
    } else {
      return asProducts
    }
  })

  const { app } = useContext()

  const productLoadedFromServer = ssrRef<boolean>(false)

  const onProductsLoaded = () => {
    if (process.server) {
      productLoadedFromServer.value = true
    }
  }

  const refreshStocks = async (options?: any) => {
    options = Object.assign({
      forceRefresh: false,
    }, options)

    if (!products.value?.length) return
    if (!options.forceRefresh && !productLoadedFromServer.value) return

    const stockMap = await app.$api.product.getStocks(
      products.value.map(product => product.id)
    )
    if (Object.keys(stockMap)) {
      products.value.forEach(product => {
        const stockRefresh = stockMap[product.id.toString()]
        if (stockRefresh) {
          product.stock = stockRefresh.stock
          Object.assign(product, stockRefresh.availibility)
        }
      })
    }
  }

  return {
    productLoadedFromServer: readonly(productLoadedFromServer),

    onProductsLoaded,
    refreshStocks
  }

}